<template>
  <div class="favourites-page" :style="{ backgroundImage: `url(${designUrl}${isMobile ? 'UserFavourites_phone.png' : 'UserFavourites.jpeg'})` }">
    <div class="sidebar-wrapper">
      <button class="hamburger" @click="sidebarOpen = !sidebarOpen">☰</button>
      <div class="sidebar" :class="{ open: sidebarOpen }">
        <div class="sidebar-header">
          <button class="close-btn" @click="sidebarOpen = false">✖</button>
          <p>{{ user.fullname }}</p>
          <p>{{ user.phone_number }}</p>
        </div>
        <ul class="sidebar-menu">

          <template v-if="user && user.user_type === 'Pet Owner'">
            <li @click="goToPage('DashboardPage')">Browse Pets</li>
            <li class="has-submenu" @click="petsOpen = !petsOpen">
              My Pets
              <span class="arrow" :class="{ 'arrow-open': petsOpen }">›</span>
            </li>
            <ul class="sub-menu" :class="{ 'sub-open': petsOpen }">
              <li @click="goToPage('YourPetsPage')">View My Pets</li>
              <li @click="goToPage('AddPetPage')">Add Pet</li>
              <li @click="goToPage('MeetingsPage')">Booking Requests</li>
            </ul>
            <li @click="goToPage('EditUserDashboard')">My Profile</li>
            <li @click="goToPage('LogoutPage')">Log Out</li>
          </template>

          <template v-else>
            <li @click="goToPage('DashboardPage')">Browse Pets</li>
            <li @click="goToPage('CalendarPage')">My Calendar</li>
            <li @click="goToPage('EditUserDashboard')">My Profile</li>
            <li @click="goToPage('LogoutPage')">Log Out</li>
          </template>

        </ul>
      </div>
    </div>

    <div class="page-wrapper">

      <div class="page-header">
        <h1 class="page-title">My Favourites</h1>
        <p class="page-sub">Pets you've saved to revisit</p>
      </div>

      <div v-if="pets.length === 0" class="empty-state">
        <div class="empty-icon">🤍</div>
        <p class="empty-text">No favourites yet</p>
        <p class="empty-sub">Tap the heart on any pet to save them here</p>
      </div>

      <div v-else class="fav-grid">
        <div v-for="pet in pets" :key="pet.id" class="fav-card">

          <div class="card-img-wrap">
            <img
              :src="backendUrl + '/pet-pics/' + pet.pet_photo"
              :alt="pet.pet_name"
              class="card-img"
            />
            <button class="heart-btn" @click="$emit('toggle-favourite', pet.id)">❤️</button>
          </div>

          <div class="card-body">
            <p class="pet-name">{{ pet.pet_name }}</p>
            <p class="pet-breed">{{ pet.breed }}</p>

            <div class="badges">
              <span class="badge" :class="pet.gender === 'Male' ? 'badge-male' : 'badge-female'">
                {{ pet.gender }}
              </span>
              <span class="badge badge-age">{{ pet.age }}</span>
            </div>

            <button class="view-btn" @click="$emit('view-pet-profile', pet)">
              View Profile
            </button>
          </div>

        </div>
      </div>

    </div>
  </div>
</template>


<script>
import axios from 'axios';
const backendUrl = process.env.VUE_APP_API_URL;

export default {
  props: {
    user: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      pets: [],
      sidebarOpen: false,
      favourites: [],
      backendUrl: process.env.VUE_APP_API_URL,
      designUrl: '/background_designs/',
      isMobile: window.innerWidth <= 480,
      petsOpen: false,
    };
  },
  methods: {
    goToPage(pageName) {
      this.sidebarOpen = false;
      this.$emit('change-page', pageName);
    },
    isFavourite(petId) {
      return Array.isArray(this.favourites) && this.favourites.includes(petId);
    },
    handleResize() {
      this.isMobile = window.innerWidth <= 480;
    },
  },
  watch: {
    user: {
      immediate: true,
      handler(newUser) {
        if (newUser && newUser.id) {
          axios.get(`${backendUrl}/api/pets/users/${newUser.id}/favourites`, {
            withCredentials: true
          })
          .then(res => {
            this.pets = res.data;
          })
          .catch(err => {
            console.error('Failed to load favourites:', err);
          });
        }
      }
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.addEventListener('resize', this.handleResize);
  },
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.favourites-page {
  min-height: 100vh;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

/* ── Page wrapper ── */
.page-wrapper {
  max-width: 1200px;
  margin: 0 auto;
  padding: 60px 20px 40px;
}

/* ── Page header ── */
.page-header { margin-bottom: 28px; }

.page-title {
  font-size: 26px;
  font-weight: 800;
  color: #1a1a1a;
  letter-spacing: -0.4px;
}

.page-sub {
  font-size: 14px;
  color: #aaa;
  font-weight: 500;
  margin-top: 4px;
}

/* ── Grid ── */
.fav-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
}

/* ── Card ── */
.fav-card {
  background: #ffffff;
  border-radius: 24px;
  border: 1px solid #f0e0ff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 16px rgba(186, 113, 255, 0.07);
  transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.25s ease;
}

.fav-card:hover {
  transform: translateY(-6px) scale(1.01);
  box-shadow: 0 16px 36px rgba(186, 113, 255, 0.13);
}

.card-img-wrap {
  position: relative;
  width: 100%;
  height: 170px;
  background: #f7f3ee;
  overflow: hidden;
}

.card-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.4s ease;
}

.fav-card:hover .card-img { transform: scale(1.06); }

.heart-btn {
  position: absolute;
  top: 10px;
  right: 11px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(6px);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.10);
  transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.heart-btn:hover { transform: scale(1.2); }

.card-body {
  padding: 14px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex: 1;
}

.pet-name {
  font-size: 16px;
  font-weight: 800;
  color: #1a1a1a;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: -0.2px;
}

.pet-breed {
  font-size: 12px;
  color: #aaa;
  font-weight: 600;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.badges { display: flex; gap: 5px; flex-wrap: wrap; margin-top: 4px; }

.badge {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
  display: inline-block;
  letter-spacing: 0.01em;
}

.badge-male   { background: #eff6ff; color: #1d4ed8; }
.badge-female { background: #fdf2f8; color: #be185d; }
.badge-age    { background: #f3eeff; color: #6d28d9; }

.view-btn {
  margin-top: 10px;
  padding: 10px 14px;
  background: #eac7ff;
  color: #4a1070;
  border: none;
  border-radius: 14px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  width: 100%;
  transition: background 0.18s, transform 0.1s;
  letter-spacing: 0.02em;
}

.view-btn:hover  { background: #ba71ff; color: #fff; }
.view-btn:active { transform: scale(0.97); }

/* ── Empty state ── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  gap: 10px;
}

.empty-icon { font-size: 48px; opacity: 0.5; }
.empty-text { font-size: 17px; font-weight: 700; color: #333; margin: 0; }
.empty-sub  { font-size: 13px; color: #bbb; margin: 0; }

/* ── Responsive ── */
@media (max-width: 700px) {
  .fav-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
  .card-img-wrap { height: 135px; }
  .pet-name { font-size: 14px; }
}

@media (max-width: 400px) {
  .fav-grid { grid-template-columns: 1fr; }
}
</style>